<template>
  <div class="mt-3 px-1.5 gap-3 flex flex-col">
    <div>
      <div class="form-line">
        <img src="@/assets/my-calendar/calendar/card.svg" alt="card" class="mt-1"/>
        <Input
          v-model="editedTitle"
          data-testid="my-calendar.schedule-appointment-panel.title-input"
          class="w-full description-input"
          :placeholder="autoScheduleTitle"
          @input="isTitleDirty = true"
        />
      </div>
    </div>
    <!-- description -->
    <div v-if="originalEventInfo?.notes">
      <div class="form-line">
        <img src="@/assets/my-calendar/calendar/subject.svg" alt="subject" class="mt-1"/>
        <div data-testid="my-calendar.schedule-appointment-panel.notes-text" class="w-full text-sm text-[#333] leading-6 tracking-[0.15px]">
          {{ originalEventInfo.notes }}
        </div>
      </div>
    </div>
    <!-- invite patient and provider -->
    <div>
      <div class="form-line">
        <img class="mt-2" src="@/assets/my-calendar/calendar/people.svg" alt="people" />
        <div class="w-full">
          <InviteSelect
            v-model="selectedPatient"
            data-testid="my-calendar.schedule-appointment-panel.patient-select"
            placeholder="Invite Patient (Optional)"
            :options="patientsList"
            :disable-dropdown="disablePatientSelector"
            header="Suggested Patients"
            class="w-full patient-select mb-2"
          />
          <InviteSelect
            v-model="selectedProviders"
            data-testid="my-calendar.schedule-appointment-panel.providers-select"
            placeholder="Invite other Providers (Optional)"
            :options="providersList"
            :multiple="true"
            header="Suggested Providers"
            class="w-full provider-select"
          />
          <div v-if="showSuggestedTimes">
            <div class="my-2">Suggested Times</div>
            <div class="flex gap-2">
              <div
                v-for="(time, idx) in suggestedTimeList"
                :key="idx"
                :data-testid="`my-calendar.schedule-appointment-panel.suggested-time-option.${time.start_time}`"
                :class="['suggested-time', selectedSuggestedTimeId === idx ? 'selected' : '']" 
                @click="selectSuggestedTime(time, idx)"
              >
                <div>
                  {{ formatSuggestedTime(time,'date') }}
                </div>
                <div>
                  <span>{{ formatSuggestedTime(time,'start_time') }} - {{ formatSuggestedTime(time,'end_time') }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- date -->
    <div>
      <div class="form-line">
        <img class="mt-3" src="@/assets/my-calendar/calendar/clock.svg" alt="clock" />
        <div>
          <div>
            <div class="flex gap-3 align-center">
              <div style="margin-top: 5px;">
                <DatePicker
                  v-model="selectedDate"
                  test-id="my-calendar.schedule-appointment-panel.start-date-picker"
                  :default="defaultDate"
                  :limit="dateLimit"
                  question=""
                  placeholder="Pick a Date"
                  class="calendar-date-picker"
                  @handleDate="handleDateChange"
                />
              </div>
              <div class="w-[120px]">
                <SingleSelector
                  data-testid="my-calendar.schedule-appointment-panel.start-time-select"
                  class="time-select"
                  :placeholder="'Select'"
                  :options="timeOptions"
                  size="medium"
                  :model-value="selectedStartTime"
                  @update:model-value="handleStartTimeChange"
                />
              </div>
              <template v-if="!showTimezoneSelectors">
                <div class="divider-rect"></div>
                <div class="w-[120px]">
                  <SingleSelector
                    data-testid="my-calendar.schedule-appointment-panel.end-time-select"
                    class="time-select"
                    :placeholder="'Select'"
                    :options="filteredTimeOptions"
                    size="medium"
                    :model-value="selectedEndTime"
                    @update:model-value="handleEndTimeChange"
                  />
                </div>
              </template>
              <template v-else>
                <div class="w-[159px]">
                  <SingleSelector
                    data-testid="my-calendar.schedule-appointment-panel.start-timezone-select"
                    class="time-select timezone-select"
                    :placeholder="'Start Time Zone'"
                    :options="timezoneOptions"
                    size="medium"
                    :model-value="selectedStartTimezone"
                    @update:model-value="handleStartTimezoneChange"
                  />
                </div>
              </template>
              <div data-testid="my-calendar.schedule-appointment-panel.toggle-timezone-btn" class="flex align-center timezone-icon-button" @click="toggleTimezoneSelectors">
                <img
                  src="@/assets/my-calendar/calendar/timezone-icon.svg"
                  alt="timezone"
                  class="timezone-icon"
                />
              </div>
            </div>
            <transition name="timezone-expand">
              <div v-if="showTimezoneSelectors" class="timezone-expanded">
                <div class="timezone-row">
                  <div style="margin-top: 5px;">
                    <DatePicker
                      v-model="selectedDate"
                      test-id="my-calendar.schedule-appointment-panel.end-date-picker"
                      :default="defaultDate"
                      :limit="dateLimit"
                      question=""
                      class="calendar-date-picker"
                      @handleDate="handleDateChange"
                    />
                  </div>
                  <div class="w-[120px]">
                    <SingleSelector
                      data-testid="my-calendar.schedule-appointment-panel.end-time-with-timezone-select"
                      class="time-select"
                      :placeholder="'Select'"
                      :options="filteredTimeOptions"
                      size="medium"
                      :model-value="selectedEndTime"
                      @update:model-value="handleEndTimeChange"
                    />
                  </div>
                  <div class="w-[159px]">
                    <SingleSelector
                      data-testid="my-calendar.schedule-appointment-panel.end-timezone-select"
                      class="time-select timezone-select"
                      :placeholder="'End Time Zone'"
                      :options="timezoneOptions"
                      size="medium"
                      :model-value="selectedEndTimezone"
                      @update:model-value="handleEndTimezoneChange"
                    />
                  </div>
                </div>
              </div>
            </transition>

          </div>
          
          <div v-if="!showTimezoneSelectors" data-testid="my-calendar.schedule-appointment-panel.all-day-checkbox" class="inline-flex items-center cursor-pointer mt-2" @click="toggleCheckbox('allDayStatus')">
            <Checkbox :status="allDayStatus" :text="'All Day'"/>
          </div>

          <!-- recurrence -->
          <div class="recurrence-row mt-2">
            <div class="repeat-row">
              <SingleSelector
                data-testid="my-calendar.schedule-appointment-panel.repeat-select"
                class="repeat-select"
                :placeholder="'Does not repeat'"
                :options="repeatOptions"
                size="medium"
                :model-value="selectedRepeat"
                @update:model-value="handleRepeatChange"
              />
            </div>
            <template v-if="selectedRepeat !== 'does_not_repeat'">
              <template v-if="endDatePickerVisible || selectedRepeatUntil">
                <span class="until-label">Until</span>
                <div class="until-date-picker">
                  <DatePicker
                    v-model="selectedRepeatUntil"
                    test-id="my-calendar.schedule-appointment-panel.repeat-until-date-picker"
                    question=""
                    placeholder="End date"
                    class="calendar-date-picker"
                    @handleDate="handleRepeatUntilChange"
                  />
                </div>
                <el-tooltip content="Remove End Date" placement="top">
                  <img
                    data-testid="my-calendar.schedule-appointment-panel.remove-end-date-btn"
                    src="@/assets/MyPractice/delete_24px.svg"
                    alt="remove end date"
                    class="remove-recurrence-icon"
                    @click="removeEndDate"
                  />
                </el-tooltip>
              </template>
              <span v-else data-testid="my-calendar.schedule-appointment-panel.choose-end-date-btn" class="choose-end-date-link" @click="chooseEndDate">
                Choose End Date
              </span>
            </template>
          </div>

        </div>

      </div>
    </div>
    <!-- meeting type -->
    <div>
      <div class="form-line"> 
        <img class="mt-1" src="@/assets/my-calendar/calendar/rate-review.svg" alt="rate-review" />
        <SingleSelector
          data-testid="my-calendar.schedule-appointment-panel.appointment-type-select"
          class="w-full type-select"
          :placeholder="'Select Appointment Type'"
          :options="meetingTypeOptions"
          size="medium"
          :model-value="selectedMeetingType"
          @update:model-value="handleMeetingTypeChange"
          />
      </div>
    </div>
    <!-- location and video conferencing -->
    <div>
      <div class="form-line">
        <img class="mt-1" src="@/assets/my-calendar/calendar/location.svg" alt="location" />
        <div class="w-full">
          <Input
            v-model="editedLocation"
            data-testid="my-calendar.schedule-appointment-panel.location-input"
            class="w-full description-input"
            placeholder="Add a room or location"
            @input="isLocationDirty = true"
          />
        </div>
      </div>
    </div>
    <!-- zoom video conferencing -->
    <div>
      <div class="form-line">
        <img class="mt-0.5" src="@/assets/my-calendar/calendar/zoom.svg" alt="zoom" />
        <div class="w-full flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <el-tooltip
              :disabled="isZoomAuthenticated"
              content="Please connect your Zoom account to enable video conferencing"
              placement="top"
            >
              <div 
                v-loading="isCreatingZoomLink"
                :class="{ 'cursor-not-allowed': !isZoomAuthenticated }"
              >
                <Checkbox
                  data-testid="my-calendar.schedule-appointment-panel.zoom-checkbox"
                  :status="videoConferencingStatus" 
                  :disabled="!isZoomAuthenticated"
                  :text="'Add Zoom Meet video conferencing'" 
                  @changeStatus="toggleCheckbox('videoConferencingStatus')"
                />
              </div>
            </el-tooltip>
          </div>
          <div v-if="videoConferencingStatus === 'checked' && zoomMeetingUrl">
            <a data-testid="my-calendar.schedule-appointment-panel.zoom-link" :href="zoomMeetingUrl" target="_blank" class="text-sm text-[#1e4670] underline">
              {{ zoomMeetingUrl }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- internal notes (internal-only, never shown to patients) -->
    <div v-if="showInternalNotes">
      <div class="form-line">
        <img class="mt-1" src="@/assets/my-calendar/calendar/lock.svg" alt="lock" />
        <div class="w-full">
          <el-input
            v-model="internalNotes"
            type="textarea"
            data-testid="my-calendar.schedule-appointment-panel.internal-notes-input"
            class="internal-notes-input"
            :autosize="{ minRows: 1, maxRows: 4 }"
            :maxlength="INTERNAL_NOTES_MAX_LENGTH"
            resize="none"
            placeholder="Add Internal Notes - Patient will NOT see this content"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { getTimeOptions } from '@/views/my-calendar/mock-data'
import moment from 'moment-timezone';

import DatePicker from '@/views/my-calendar/components/DatePicker.vue';
import InviteSelect from './schedule-components/InviteSelect.vue';
import Input from '@/NewLibrary/Input.vue';
import Checkbox from '@/NewLibrary/Checkbox.vue';
import { SHORT_TIMEZONE_OPTIONS, normalizeTimezoneValue, getTimezoneOptionsWithValue } from '@/utils/timezones';
import { notifyUnreportedError } from '@/utils/notify';
import { useStore } from 'vuex'
const store = useStore()
const emit = defineEmits(['update:disabled'])
const props = defineProps({
  activeTab: {
    type: String,
    default: 'appointment'
  },
  disablePatientSelector: {
    type: Boolean,
    default: false,
  }
})

const originalEventInfo = computed(() => store.state.calendar.currentEventInfo)
const currentEventInfo = ref({ ...originalEventInfo });
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const userSettings = computed(() => store.getters['calendar/userSettings'])
const eventTypeList = computed(() => store.getters['calendar/rawEventTypeList'] || [])
const defaultAppointmentDuration = computed(() => (
  String(userSettings.value?.default_appointment_duration || '60')
))
const timeOptions = computed(() => getTimeOptions(defaultAppointmentDuration.value))
const meetingTypeOptions = computed(() =>
  eventTypeList.value.map(({ id, name, color_hex }) => ({
    id,
    label: name,
    value: name,
    color: color_hex,
  }))
)
// Prefer the event's own boundary zone. Coercing it to the viewer's calendar zone used to
// be display-only; now that the selection is sent as `timezone` on save (VP-19022), doing so
// would re-anchor a colleague's recurring series to the editor's zone on any edit.
const resolveDisplayTimezone = (eventInfo, boundary = 'start') => {
  const explicitBoundaryTimezone = boundary === 'end'
    ? eventInfo?.end_timezone
    : eventInfo?.start_timezone

  return normalizeTimezoneValue(
    explicitBoundaryTimezone || eventInfo?.selectedTimezone || calendarTimezone.value
  )
}

// Zone the recurrence rule is expressed in. Must match the `timezone` sent on save so the
// backend expands BYDAY / BYSETPOS and the UNTIL cutoff in the same zone the rule was built in.
const recurrenceTimezone = () => selectedStartTimezone.value || calendarTimezone.value

const getWeekdayCode = (dateStr) => {
  if (!dateStr) return ''
  const m = moment.tz(dateStr, 'YYYY-MM-DD', recurrenceTimezone())
  if (!m.isValid()) return ''
  const map = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
  return map[m.day()] || ''
}

const getNthWeekdayOfMonth = (dateStr) => {
  if (!dateStr) return null
  const m = moment.tz(dateStr, 'YYYY-MM-DD', recurrenceTimezone())
  if (!m.isValid()) return null
  return Math.ceil(m.date() / 7)
}

const getOrdinalSuffix = (n) => {
  const v = n % 100
  if (v >= 11 && v <= 13) return 'th'
  switch (n % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

const repeatOptions = computed(() => {
  const weekday = getWeekdayCode(selectedDate.value)
  const nth = getNthWeekdayOfMonth(selectedDate.value)
  const weekdayName = selectedDate.value
    ? moment.tz(selectedDate.value, 'YYYY-MM-DD', recurrenceTimezone()).format('dddd')
    : ''
  const monthDayLabel = selectedDate.value
    ? moment.tz(selectedDate.value, 'YYYY-MM-DD', recurrenceTimezone()).format('MMMM D')
    : ''

  const monthlyLabel = (nth && weekdayName)
    ? `Monthly on the ${nth}${getOrdinalSuffix(nth)} ${weekdayName}`
    : 'Monthly'
  const weeklyLabel = weekdayName ? `Weekly on ${weekdayName}` : 'Weekly'
  const yearlyLabel = monthDayLabel ? `Annually on ${monthDayLabel}` : 'Annually'

  return [
    { value: 'does_not_repeat', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekday', label: 'Every weekday' },
    { value: 'weekly', label: weeklyLabel, meta: { weekday } },
    { value: 'monthly', label: monthlyLabel, meta: { weekday, nth } },
    { value: 'annually', label: yearlyLabel },
  ]
})

// Zoom authentication state
const isZoomAuthenticated = computed(() => store.state.calendar.isZoomAuthenticated)

// Date range limit: 24 hours to 30 days from now
const dateLimit = computed(() => {
  const minDate = moment().add(24, 'hours').format('YYYY-MM-DD');
  const maxDate = moment().add(30, 'days').format('YYYY-MM-DD');
  return [minDate, maxDate];
})

// Use Vuex getters for cached formatted lists - computed only once in store
const providersList = computed(() => store.getters['calendar/formattedProvidersList'])
const patientsList = computed(() => store.getters['calendar/formattedPatientsList'])
const locationsList = computed(() =>
  store.state.calendar.rawLocationList.map(({ location_name, street_address }) => ({
    id: [location_name, street_address].filter(Boolean).join(' - '),
    name: [location_name, street_address].filter(Boolean).join(' - ')
  }))
)

const zoomMeetingUrl = ref(originalEventInfo.value?.zoomMeetingUrl || originalEventInfo.value?.external_url || originalEventInfo.value?.meetingUrl || '')
const videoConferencingStatus = ref(
  (originalEventInfo.value?.videoConferencingStatus === 'checked' || 
   originalEventInfo.value?.external_url || 
   originalEventInfo.value?.zoomMeetingUrl) ? 'checked' : 'uncheck'
)
const isCreatingZoomLink = ref(false)
const allDayStatus = ref('uncheck')
const selectedSuggestedTimeId = ref(null)
const isFromSuggestedTime = ref(false)
const isUpdatingFromStore = ref(false) // Flag to prevent watch loops
const showTimezoneSelectors = ref(false)
const selectedRepeat = ref('does_not_repeat')
const selectedRepeatUntil = ref('')
const endDatePickerVisible = ref(false)
const selectedMeetingType = ref(originalEventInfo.value?.meetingType ?? '');
const selectedStartTimezone = ref(resolveDisplayTimezone(originalEventInfo.value, 'start'));
const selectedEndTimezone = ref(resolveDisplayTimezone(originalEventInfo.value, 'end'));
// The event's own zone is saved verbatim (see getTimezoneOptionsWithValue), so a stored
// zone outside the curated list (e.g. America/Detroit) must still be selectable.
const timezoneOptions = computed(() => getTimezoneOptionsWithValue(
  selectedEndTimezone.value,
  getTimezoneOptionsWithValue(selectedStartTimezone.value, SHORT_TIMEZONE_OPTIONS)
))

const getDateKey = (value, timezone = calendarTimezone.value) => value ? moment.utc(value).tz(timezone).format('YYYY-MM-DD') : ''
const getTimeLabel = (value, timezone = calendarTimezone.value) => value ? moment.utc(value).tz(timezone).format('hh:mma') : ''

const defaultDate = computed(() => {
  if (!originalEventInfo?.value?.start || !originalEventInfo?.value?.end) return '';
  return getDateKey(originalEventInfo?.value?.start);
});

const selectedDate = ref(originalEventInfo.value?.selectedDate || defaultDate.value)
const selectedPatient = ref(originalEventInfo.value?.selectedPatient || '');
const selectedProviders = ref(originalEventInfo.value?.selectedProviders || []);

// Initialize time fields from store if available
const selectedStartTime = ref(originalEventInfo.value?.selectedStartTime || '');
const selectedEndTime = ref(originalEventInfo.value?.selectedEndTime || '');

const buildAutoScheduleTitle = (meetingType = selectedMeetingType.value, patientId = selectedPatient.value) => {
  if (!meetingType && !patientId) {
    return 'Title';
  }

  let patientName = '';
  if (patientId) {
    const patient = patientsList.value.find(p => p.id === patientId);
    patientName = patient ? patient.name : '';
  }

  if (patientName) {
    const [firstName, lastName = ''] = patientName.split(' ');
    const firstNameInitial = firstName ? `${firstName.charAt(0)}.` : '';
    const formattedName = lastName ? `${firstNameInitial} ${lastName}`.trim() : '';
    return meetingType 
      ? `${meetingType}${formattedName ? ` (${formattedName})` : ''}`
      : formattedName || 'Title';
  }

  return meetingType || 'Title';
}

const autoScheduleTitle = computed(() => buildAutoScheduleTitle())
const editedTitle = ref('')
const initialEditedTitle = ref('')
const isTitleDirty = ref(false)
const buildAutoScheduleLocation = (eventLocation = originalEventInfo.value?.location) => {
  const trimmedEventLocation = (eventLocation || '').trim()
  if (trimmedEventLocation) return trimmedEventLocation

  const firstClinicLocation = locationsList.value[0]?.name || ''
  return firstClinicLocation || ''
}
const autoScheduleLocation = computed(() => buildAutoScheduleLocation())
const editedLocation = ref('')
const initialEditedLocation = ref('')
const isLocationDirty = ref(false)
const isHydratingForm = ref(false)

// Internal Notes: internal-only free text (VP-18049). Backend enforces a 2000 char limit
// and only returns the field to clinic tokens; patients never see it.
const INTERNAL_NOTES_MAX_LENGTH = 2000
const internalNotes = ref(originalEventInfo.value?.internal_notes || '')
const initialInternalNotes = ref(originalEventInfo.value?.internal_notes || '')
// The meeting-request reschedule flow goes through a different mutation whose input
// does not accept internal_notes, so the field is only offered for create/edit.
const showInternalNotes = computed(() => originalEventInfo.value?.mode !== 'reschedule')
const normalizedInternalNotes = computed(() => (internalNotes.value || '').trim())
// null clears the stored value on the backend; an empty string would be stored as-is.
const internalNotesPayload = computed(() => normalizedInternalNotes.value || null)
const internalNotesChanged = computed(
  () => normalizedInternalNotes.value !== (initialInternalNotes.value || '').trim()
)

const scheduleTitle = computed(() => {
  const trimmed = (editedTitle.value || '').trim()
  return trimmed || autoScheduleTitle.value
});

const scheduleLocation = computed(() => {
  const trimmed = (editedLocation.value || '').trim()
  return trimmed || autoScheduleLocation.value
})

watch(autoScheduleTitle, (newTitle, oldTitle) => {
  const currentTitle = (editedTitle.value || '').trim()
  const previousTitle = (oldTitle || '').trim()
  const isEditMode = originalEventInfo.value?.mode === 'edit'
  if ((!isTitleDirty.value && !isEditMode) || !currentTitle || currentTitle === previousTitle) {
    editedTitle.value = newTitle || 'Title'
  }
})

watch(autoScheduleLocation, (newLocation, oldLocation) => {
  const currentLocation = (editedLocation.value || '').trim()
  const previousLocation = (oldLocation || '').trim()
  if (!isLocationDirty.value || !currentLocation || currentLocation === previousLocation) {
    editedLocation.value = newLocation || ''
  }
})

const handleStartTimeChange = (value) => { selectedStartTime.value = value }
const handleEndTimeChange = (value) => { selectedEndTime.value = value }
const handleStartTimezoneChange = (value) => { selectedStartTimezone.value = value }
const handleEndTimezoneChange = (value) => { selectedEndTimezone.value = value }
const handleRepeatChange = (value) => {
  selectedRepeat.value = value
  // Never pre-set an end date; the picker stays hidden until the user chooses one
  if (value === 'does_not_repeat') {
    selectedRepeatUntil.value = ''
    endDatePickerVisible.value = false
  }
}

const handleRepeatUntilChange = (value) => { selectedRepeatUntil.value = value }

// Trash icon clears the end date (recurrence stays active) -> shows "Choose End Date"
const removeEndDate = () => {
  selectedRepeatUntil.value = ''
  endDatePickerVisible.value = false
}

// "Choose End Date" link reveals an empty date picker (no pre-set value)
const chooseEndDate = () => {
  endDatePickerVisible.value = true
}
const toggleTimezoneSelectors = () => {
  showTimezoneSelectors.value = !showTimezoneSelectors.value
}

// Build the UNTIL token e.g. 20271023T235959Z. The chosen end date is interpreted as
// end-of-day in the event's zone (then converted to UTC) so occurrences are not
// cut off early for non-UTC users due to timezone/DST offsets.
// Returns null when no end date is chosen - a recurrence requires an explicit end date.
const buildUntilToken = (untilDateStr) => {
  if (!untilDateStr) return null
  const m = moment.tz(untilDateStr, 'YYYY-MM-DD', recurrenceTimezone())
  if (!m.isValid()) return null
  return m.endOf('day').utc().format('YYYYMMDDTHHmmss[Z]')
}

// Produces a single-line rrule string e.g.
// FREQ=MONTHLY;BYDAY=TH;BYSETPOS=4;UNTIL=20271023T235959Z;DTSTART=20260923T130000Z
const buildRrule = (repeatValue, dateStr, untilDateStr = selectedRepeatUntil.value) => {
  if (!repeatValue || repeatValue === 'does_not_repeat') return null
  const weekday = getWeekdayCode(dateStr)
  const nth = getNthWeekdayOfMonth(dateStr)
  const m = dateStr ? moment.tz(dateStr, 'YYYY-MM-DD', recurrenceTimezone()) : null
  if (!m || !m.isValid()) return null

  const startIsoUtc = toISO(selectedDate.value, selectedStartTime.value, selectedStartTimezone.value)
  if (!startIsoUtc) return null
  const dtstart = moment.utc(startIsoUtc).format('YYYYMMDDTHHmmss[Z]')

  const until = buildUntilToken(untilDateStr)
  if (!until) return null

  let freqPart = null
  if (repeatValue === 'daily') freqPart = 'FREQ=DAILY'
  if (repeatValue === 'weekday') freqPart = 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'
  if (repeatValue === 'weekly' && weekday) freqPart = `FREQ=WEEKLY;BYDAY=${weekday}`
  if (repeatValue === 'monthly' && weekday && nth) freqPart = `FREQ=MONTHLY;BYDAY=${weekday};BYSETPOS=${nth}`
  if (repeatValue === 'annually') freqPart = `FREQ=YEARLY;BYMONTH=${m.month() + 1};BYMONTHDAY=${m.date()}`
  if (!freqPart) return null

  return `${freqPart};UNTIL=${until};DTSTART=${dtstart}`
}

const rruleValue = computed(() => buildRrule(selectedRepeat.value, selectedDate.value))

const resolveRepeatFromRrule = (rrule) => {
  if (!rrule) return 'does_not_repeat'
  const raw = String(rrule).trim()
  const rrulePart = raw.toUpperCase().includes('RRULE:')
    ? raw.split(/RRULE:/i).pop()
    : raw
  const normalized = String(rrulePart).trim().toUpperCase()
  if (normalized.startsWith('FREQ=DAILY')) return 'daily'
  if (normalized.startsWith('FREQ=WEEKLY') && normalized.includes('BYDAY=MO,TU,WE,TH,FR')) return 'weekday'
  if (normalized.startsWith('FREQ=WEEKLY') && normalized.includes('BYDAY=')) return 'weekly'
  if (normalized.startsWith('FREQ=MONTHLY')) return 'monthly'
  if (normalized.startsWith('FREQ=YEARLY')) return 'annually'
  return 'does_not_repeat'
}

// Extract the UNTIL end date (as YYYY-MM-DD) from an existing rrule for edit mode
const resolveUntilFromRrule = (rrule) => {
  if (!rrule) return ''
  const match = String(rrule).toUpperCase().match(/UNTIL=(\d{8})/)
  if (!match) return ''
  const m = moment.utc(match[1], 'YYYYMMDD')
  return m.isValid() ? m.format('YYYY-MM-DD') : ''
}

// end time options
const filteredTimeOptions = computed(() => {
  if (!selectedStartTime.value) return timeOptions.value;
  const startIndex = timeOptions.value.findIndex(opt => opt.value === selectedStartTime.value);
  return startIndex >= 0 ? timeOptions.value.slice(startIndex + 1) : timeOptions.value;
});

onMounted(async () => {
  if (!userSettings.value) {
    await store.dispatch('calendar/getUserSettings');
  }
  if (!eventTypeList.value.length) {
    await store.dispatch('calendar/fetchPracticeEventTypes');
  }
})

const handleDateChange = (newDate) => {
  // console.log(newDate, 'newDate');
  if (!currentEventInfo?.value?.start || !currentEventInfo?.value?.end) return;

  const dateStr = newDate;
  const currentStartDate = getDateKey(currentEventInfo?.value?.start);

  // Skip if the date hasn't actually changed
  if (dateStr === currentStartDate) {
    // console.log('Date unchanged, skipping handleDateChange');
    return;
  }

  const startTime = getTimeLabel(currentEventInfo?.value?.start, selectedStartTimezone.value);
  const endTime = getTimeLabel(currentEventInfo?.value?.end, selectedEndTimezone.value);
  currentEventInfo.value.start = moment.tz(`${dateStr} ${startTime}`, 'YYYY-MM-DD hh:mma', selectedStartTimezone.value).utc().toISOString();
  currentEventInfo.value.end = moment.tz(`${dateStr} ${endTime}`, 'YYYY-MM-DD hh:mma', selectedEndTimezone.value).utc().toISOString();
};

const handleMeetingTypeChange = (value) => {
  selectedMeetingType.value = value;
}
const toggleCheckbox = async (status) => {
  if(status === 'allDayStatus') {
    allDayStatus.value = allDayStatus.value === 'checked' ? 'uncheck' : 'checked';
    selectedStartTime.value = '09:00am';
    selectedEndTime.value = '05:00pm';
  } else if(status === 'videoConferencingStatus') {
    // Toggle only. The Zoom meeting is no longer pre-generated here; it is created
    // as part of the final submit payload (see ensureZoomMeetingUrl).
    // We intentionally do NOT clear zoomMeetingUrl on uncheck so that re-checking
    // restores the existing link instead of spawning a new meeting on submit.
    // Whether the link is actually saved is decided at submit time from the status.
    videoConferencingStatus.value = videoConferencingStatus.value === 'checked' ? 'uncheck' : 'checked';
  }
}

// Creates the Zoom meeting at final submit time. Guarded against duplicate meeting
// creation: returns the existing URL when one is already present (e.g. editing an
// event that already has a link) and short-circuits while a request is in flight.
const ensureZoomMeetingUrl = async () => {
  if (videoConferencingStatus.value !== 'checked') return '';
  if (zoomMeetingUrl.value) return zoomMeetingUrl.value;
  if (isCreatingZoomLink.value) return zoomMeetingUrl.value;

  isCreatingZoomLink.value = true;
  try {
    const startTime = toISO(selectedDate.value, selectedStartTime.value, selectedStartTimezone.value);
    const endTime = toISO(selectedDate.value, selectedEndTime.value, selectedEndTimezone.value);
    const duration = (startTime && endTime)
      ? Math.max(moment.utc(endTime).diff(moment.utc(startTime), 'minutes'), 0)
      : 0;

    const zoomResult = await store.dispatch('calendar/createZoomMeeting', {
      topic: scheduleTitle.value,
      startTime,
      duration,
      timezone: selectedStartTimezone.value,
    });
    if (zoomResult && zoomResult.success && zoomResult.meetingUrl) {
      zoomMeetingUrl.value = zoomResult.meetingUrl;
    } else {
      console.log('Zoom creation failed or no URL returned');
    }
  } finally {
    isCreatingZoomLink.value = false;
  }
  return zoomMeetingUrl.value;
}

const toISO = (dateStr, timeStr, timezone = calendarTimezone.value) => {
  return moment.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD hh:mma', timezone).utc().toISOString();
};

const selectedMeetingTypeColor = computed(() =>
  eventTypeList.value.find(({ name }) => name === selectedMeetingType.value)?.color_hex || ''
)

const selectedPracticeEventType = computed(() => {
  const matchedByName = eventTypeList.value.find(({ name }) => name === selectedMeetingType.value)
  if (matchedByName) return matchedByName

  const originalPracticeEventTypeId = originalEventInfo.value?.practice_event_type_id
  if (originalPracticeEventTypeId) {
    return eventTypeList.value.find(({ id }) => id === originalPracticeEventTypeId) || null
  }

  return null
})

const selectedPracticeEventTypeId = computed(() => selectedPracticeEventType.value?.id || null)

const participants = computed(() => {
  // provider participants list
  const providerParticipants = selectedProviders.value
    .map(providerId => {
      const provider = providersList.value.find(p => p.id === providerId);
      if (provider && provider.calendar_id) {
        return {
          participant_calendar_id: provider.calendar_id,
          role: 'provider'
        };
      }
      return null;
    })
    .filter(Boolean);

  let patientParticipant = null;
  if (selectedPatient.value) {
    const patient = patientsList.value.find(p => p.id === selectedPatient.value);
    if (patient && patient.calendar_id) {
      patientParticipant = {
        participant_calendar_id: patient.calendar_id,
        role: 'patient'
      };
    }
  }

  return patientParticipant
    ? [...providerParticipants, patientParticipant]
    : providerParticipants;
});

const normalizeParticipantCompareRole = (role) => {
  if (role === 'provider' || role === 'clinicadmin') return 'provider'
  return role
}

const isSameParticipantForCompare = (left, right) => (
  left?.participant_calendar_id === right?.participant_calendar_id &&
  normalizeParticipantCompareRole(left?.role) === normalizeParticipantCompareRole(right?.role)
)

const createOrUpdateEventAction = computed(() => store.state.calendar.createOrUpdateEventAction)

// Watch for create/update event action trigger from parent component
// When triggered, collects all form data and dispatches appropriate action based on mode:
// - 'edit': Updates existing event with new data
// - 'reschedule': Updates meeting request status and reschedules
// - default: Creates new event with current form values
// After completion, resets action flag and refreshes event list
watch(createOrUpdateEventAction, async (newVal) => {
  if (!newVal || props.activeTab !== 'appointment') return

  let submitFailed = false
  store.commit('calendar/SET_LAST_SUBMIT_FAILED', false)
  try {
    // Ensure selected patient has a calendar_id before building participants.
    // clinic_patients may now come back without calendar_id; create one on demand.
    if (selectedPatient.value) {
      const patient = patientsList.value.find(p => p.id === selectedPatient.value);
      if (patient && !patient.calendar_id) {
        await store.dispatch('calendar/ensurePatientCalendarId', selectedPatient.value);
      }
    }
    // Create the Zoom meeting now (final submit) rather than pre-generating on toggle.
    // Returns the (existing or newly created) link when checked, or '' when unchecked
    // so unchecking removes the Zoom link from the event.
    const submittedZoomUrl = await ensureZoomMeetingUrl();
    const eventInfo = {
      event_title: scheduleTitle.value,
      start_time: toISO(selectedDate.value, selectedStartTime.value, selectedStartTimezone.value),
      end_time: toISO(selectedDate.value, selectedEndTime.value, selectedEndTimezone.value),
      practice_event_type_id: selectedPracticeEventTypeId.value,
      location: scheduleLocation.value,
      participants: participants.value,
      external_url: submittedZoomUrl,
      rrule: rruleValue.value,
      // The zone the recurrence repeats in (VP-19022). Without it the backend falls
      // back to the provider's setting and ignores the zone picked in the panel.
      timezone: selectedStartTimezone.value,
    }
    // Only send internal_notes when the user actually changed it. Omitting the key leaves
    // the stored value untouched, so an event whose notes never hydrated cannot blank out
    // notes written by someone else (the backend treats an explicit null as "clear").
    if (internalNotesChanged.value) {
      eventInfo.internal_notes = internalNotesPayload.value
    }

    if (originalEventInfo.value?.mode === 'edit') {
      await store.dispatch('calendar/updateEvent', {
        event_id: originalEventInfo.value.event_id,
        ...eventInfo
      })
    } else if (originalEventInfo.value?.mode === 'reschedule') {
      // provider reschedule patient meeting request
      const eventInput = {
        event_title: scheduleTitle.value,
        start_time: toISO(selectedDate.value, selectedStartTime.value, selectedStartTimezone.value),
        end_time: toISO(selectedDate.value, selectedEndTime.value, selectedEndTimezone.value),
        practice_event_type_id: selectedPracticeEventTypeId.value,
        location: scheduleLocation.value,
        practice_id: originalEventInfo.value.practice_id,
        creator_calendar_id: store.state.calendar.creatorCalendarId,
        participants: participants.value,
        external_url: submittedZoomUrl,
        rrule: rruleValue.value,
        timezone: selectedStartTimezone.value,
      }

      await store.dispatch('calendar/updateMeetingRequest', {
        id: originalEventInfo.value.uuid,
        status: 'reschedule',
        event_input: eventInput
      })
      await store.dispatch('calendar/fetchMeetingRequests')
    } else {
      const userInfo = store.state.login.userInfo;

      await store.dispatch('calendar/createEvent', {
        practice_id: userInfo.clinic_id,
        creator_calendar_id: store.state.calendar.creatorCalendarId,
        ...eventInfo
      })
    }
  } catch (error) {
    // Mark the failure so ScheduleDetail does not read the action flag reset below as a
    // successful submit and auto-close the modal on the reschedule path.
    submitFailed = true
    store.commit('calendar/SET_LAST_SUBMIT_FAILED', true)
    console.error('Create/update appointment failed:', error)
    notifyUnreportedError(error, 'Failed to save appointment')
  } finally {
    // Always release the submit button, including when ensurePatientCalendarId or
    // ensureZoomMeetingUrl threw before any mutation ran.
    await store.dispatch('calendar/setCreateOrUpdateEventAction', false)
  }

  if (submitFailed) return

  const userInfo = store.state.login.userInfo;
  const isClinic150105 = userInfo?.clinic_id === 150105;
  const selectedClinicianCalendarIds = store.getters['calendar/selectedClinicianCalendarIds'] || []
  const calendarIds = isClinic150105
    ? selectedClinicianCalendarIds
    : [store.state.calendar.creatorCalendarId];

  if (isClinic150105 && calendarIds.length === 0) {
    store.commit('calendar/SET_RAW_EVENT_LIST', [])
    return
  }

  store.commit('calendar/SET_CALENDAR_LOADING', true);
  await store.dispatch('calendar/fetchEvents', {
    calendar_ids: calendarIds,
    start_time: moment.utc('1970-01-01').startOf('day').toISOString(),
    end_time: moment().add(2, 'years').endOf('day').toISOString()
  })
  store.commit('calendar/SET_CALENDAR_LOADING', false);
})

// Sync form values back to store when originalEventInfo changes
watch(originalEventInfo, (newVal, oldVal) => {
  // Skip if updating from sync watch to prevent loops
  if (isUpdatingFromStore.value) {
    return;
  }

  if (!newVal) {
    editedTitle.value = ''
    initialEditedTitle.value = ''
    isTitleDirty.value = false
    editedLocation.value = ''
    initialEditedLocation.value = ''
    isLocationDirty.value = false
    internalNotes.value = ''
    initialInternalNotes.value = ''
    currentEventInfo.value = {}
    return;
  }

  currentEventInfo.value = { ...newVal }
  if (newVal) {
    selectedStartTimezone.value = resolveDisplayTimezone(newVal, 'start');
    selectedEndTimezone.value = resolveDisplayTimezone(newVal, 'end');
    
    selectedStartTime.value = getTimeLabel(newVal.start, selectedStartTimezone.value);
    selectedEndTime.value = getTimeLabel(newVal.end, selectedEndTimezone.value);
    selectedDate.value = getDateKey(newVal.start, selectedStartTimezone.value);
    // drop or resize
    if(newVal.preview_start && newVal.preview_end){
      selectedStartTime.value = getTimeLabel(newVal.preview_start, selectedStartTimezone.value);
      selectedEndTime.value = getTimeLabel(newVal.preview_end, selectedEndTimezone.value);
      selectedDate.value = getDateKey(newVal.preview_start, selectedStartTimezone.value);
    }
    // console.log(selectedDate.value,'selectedDate.value')
    handleDateChange(selectedDate.value);
    selectedMeetingType.value = newVal.meetingType;

    if(newVal.participants){
      // provider participants
      selectedProviders.value = newVal.participants
        .filter(p => ['provider', 'clinicadmin'].includes(p.role))
        .map(providerParticipant => {
          const provider = providersList.value.find(p => p.calendar_id === providerParticipant.participant_calendar_id);
          return provider ? provider.id : null;
        })
        .filter(Boolean);

      // patient participants
      const patientParticipant = newVal.participants.find(p => p.role === 'patient');
      if (patientParticipant) {
        const patient = patientsList.value.find(p => p.calendar_id === patientParticipant.participant_calendar_id);
        selectedPatient.value = patient ? patient.id : '';
      } else {
        selectedPatient.value = '';
      }
    }

    selectedRepeat.value = resolveRepeatFromRrule(newVal.rrule)
    selectedRepeatUntil.value = resolveUntilFromRrule(newVal.rrule)

    const initialTitle = (newVal.event_title || '').trim() || buildAutoScheduleTitle(newVal.meetingType, selectedPatient.value)
    editedTitle.value = initialTitle || 'Title'
    initialEditedTitle.value = initialTitle || 'Title'
    isTitleDirty.value = false

    const initialLocation = (newVal.location || '').trim() || buildAutoScheduleLocation(newVal.location)
    editedLocation.value = initialLocation || ''
    initialEditedLocation.value = initialLocation || ''
    isLocationDirty.value = false

    // internal_notes comes back as null for empty values (or when the token is not permitted)
    const initialNotes = newVal.internal_notes || ''
    internalNotes.value = initialNotes
    initialInternalNotes.value = initialNotes

  }
}, { immediate: true })


watch(selectedStartTime, (newStart, oldStart) => {
  if (newStart && selectedEndTime.value) {
    const startIndex = timeOptions.value.findIndex(opt => opt.value === newStart);
    const endIndex = timeOptions.value.findIndex(opt => opt.value === selectedEndTime.value);

    if (startIndex >= endIndex) {
      selectedEndTime.value = '';
    }
  }
});
watch([selectedStartTime, selectedEndTime], () => {
  if (isFromSuggestedTime.value) {
    isFromSuggestedTime.value = false
    return
  }
  selectedSuggestedTimeId.value = null
})

// Watch for timezone changes and recalculate times
watch(calendarTimezone, (newTimezone, oldTimezone) => {
  if (newTimezone !== oldTimezone && originalEventInfo.value) {
    // Recalculate timezone selections
    selectedStartTimezone.value = resolveDisplayTimezone(originalEventInfo.value, 'start');
    selectedEndTimezone.value = resolveDisplayTimezone(originalEventInfo.value, 'end');
    
    // Recalculate time displays with new timezone
    selectedStartTime.value = getTimeLabel(originalEventInfo.value.start, selectedStartTimezone.value);
    selectedEndTime.value = getTimeLabel(originalEventInfo.value.end, selectedEndTimezone.value);
    selectedDate.value = getDateKey(originalEventInfo.value.start, selectedStartTimezone.value);
    
    // Handle preview times if they exist
    if(originalEventInfo.value.preview_start && originalEventInfo.value.preview_end){
      selectedStartTime.value = getTimeLabel(originalEventInfo.value.preview_start, selectedStartTimezone.value);
      selectedEndTime.value = getTimeLabel(originalEventInfo.value.preview_end, selectedEndTimezone.value);
      selectedDate.value = getDateKey(originalEventInfo.value.preview_start, selectedStartTimezone.value);
    }
    
    handleDateChange(selectedDate.value);
  }
});

const disableBtn = computed(() => {
  const ori = originalEventInfo.value || {}
  const isRescheduleMode = ori.mode === 'reschedule'
  const currentTitle = (scheduleTitle.value || '').trim()
  const originalTitle = (initialEditedTitle.value || '').trim()
  const currentLocation = (scheduleLocation.value || '').trim()
  const originalLocation = (initialEditedLocation.value || '').trim()
  const missingDate = !selectedDate.value
  const missingStartTime = !selectedStartTime.value
  const missingEndTime = !selectedEndTime.value
  const missingMeetingType = !selectedMeetingType.value
  const missingParticipants = participants.value.length === 0
  // A recurring appointment must have an end date selected
  const missingRecurrenceEndDate = selectedRepeat.value !== 'does_not_repeat' && !selectedRepeatUntil.value
  const missingRequired = (
    missingDate ||
    missingStartTime ||
    missingEndTime ||
    missingRecurrenceEndDate ||
    missingMeetingType ||
    missingParticipants
  )
  const unchangedInEditMode = (
    !isRescheduleMode &&
    selectedDate.value === getDateKey(ori.start) &&
    selectedStartTime.value === getTimeLabel(ori.start, resolveDisplayTimezone(ori, 'start')) &&
    selectedEndTime.value === getTimeLabel(ori.end, resolveDisplayTimezone(ori, 'end')) &&
    selectedStartTimezone.value === resolveDisplayTimezone(ori, 'start') &&
    selectedEndTimezone.value === resolveDisplayTimezone(ori, 'end') &&
    selectedMeetingType.value === ori.meetingType &&
    currentTitle === originalTitle &&
    currentLocation === originalLocation &&
    !internalNotesChanged.value &&
    zoomMeetingUrl.value === (ori.external_url || ori.meetingUrl || ori.zoomMeetingUrl || '') &&
    videoConferencingStatus.value === (ori.videoConferencingStatus === 'checked' || ori.external_url || ori.zoomMeetingUrl ? 'checked' : 'uncheck') &&
    (rruleValue.value || null) === (ori.rrule || null) &&
    participants.value.length === (ori.participants?.length || 0) &&
    participants.value.every(cur =>
      ori.participants && ori.participants.some(oriItem =>
        isSameParticipantForCompare(cur, oriItem)
      )
    ) &&
    (ori.participants || []).every(oriItem =>
      participants.value.some(cur =>
        isSameParticipantForCompare(cur, oriItem)
      )
    )
  )

  return missingRequired || unchangedInEditMode
})

const hasSelectedPatient = (value) => {
  if (value === undefined || value === null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const showSuggestedTimes = computed(() => hasSelectedPatient(selectedPatient.value))
const suggestedTimeList = computed(() => store.state.calendar.nextAvailableTimeList);

const selectSuggestedTime = (time, idx) => {
  isFromSuggestedTime.value = true;
  selectedSuggestedTimeId.value = idx;
  selectedStartTime.value = getTimeLabel(time.start_time);
  selectedEndTime.value = getTimeLabel(time.end_time);

  const date = getDateKey(time.start_time);
  selectedDate.value = date;
  handleDateChange(date);
};

const formatSuggestedTime = (time, type) => {
  if (type === 'date') {
    return getDateKey(time.start_time);
  } else if (type === 'start_time') {
    return getTimeLabel(time.start_time);
  } else if (type === 'end_time') {
    return getTimeLabel(time.end_time);
  }
}
// Watch form field changes to update suggested times and preview info
// - When patient is selected, fetches next available time slots for scheduling suggestions
// - When time fields change, updates the calendar preview with current form values
// Runs immediately on component mount to initialize preview state
watch(
  [selectedPatient, selectedDate, selectedStartTime, selectedEndTime, selectedMeetingType, selectedStartTimezone, selectedEndTimezone, editedTitle],
  ([patient, date, startTime, endTime, meetingType, startTimezone, endTimezone]) => {
    if (!originalEventInfo.value) {
      return
    }

    const isValidDate = Boolean(date && moment(date).isValid())

    if (patient && !selectedSuggestedTimeId.value && isValidDate) {
      store.dispatch('calendar/fetchNextAvailableTime', { 
        calendar_ids: [store.state.calendar.creatorCalendarId],
        duration: 30,
        start_date: moment(date).utc().toISOString(),
        end_date: moment(date).utc().add(1, 'month').toISOString(),
        count: 3,
      });
    }
    if (startTime) {
      const start = toISO(date, startTime, startTimezone);
      const end = toISO(date, endTime, endTimezone);
      // console.log(originalEventInfo.value,'originalEventInfo.value')
      store.dispatch('calendar/setPreScheduleInfo', {
        event_id: originalEventInfo.value?.event_id,
        start_time: start ? new Date(start) : null,
        end_time: end ? new Date(end) : null,
        event_type: meetingType,
        event_title: scheduleTitle.value,
        timezone: startTimezone,
        start_timezone: startTimezone,
        end_timezone: endTimezone,
      });
    }
  },
  { immediate: true }
);

watch(disableBtn, (val) => {
  emit('update:disabled', val)
}, { immediate: true })

// Sync form field changes back to store so data is preserved when expanding from preview to detail
watch(
  [selectedPatient, selectedProviders, selectedStartTime, selectedEndTime, selectedDate, selectedMeetingType, editedLocation, selectedStartTimezone, selectedEndTimezone, selectedRepeat, selectedRepeatUntil, videoConferencingStatus, zoomMeetingUrl, internalNotes],
  ([patient, providers, startTime, endTime, date, meetingType, location, startTimezone, endTimezone, repeat, repeatUntil, videoStatus, zoomUrl, notes]) => {
    // Only update if we have an existing event info (don't create new one)
    if (originalEventInfo.value && originalEventInfo.value.mode !== 'edit') {
      isUpdatingFromStore.value = true;
      const updatedInfo = {
        ...originalEventInfo.value,
        selectedPatient: patient,
        selectedProviders: providers,
        selectedStartTime: startTime,
        selectedEndTime: endTime,
        selectedDate: date,
        meetingType: meetingType,
        location: (location || '').trim(),
        timezone: startTimezone,
        start_timezone: startTimezone,
        end_timezone: endTimezone,
        rrule: buildRrule(repeat, date, repeatUntil),
        videoConferencingStatus: videoStatus,
        zoomMeetingUrl: zoomUrl,
        external_url: zoomUrl,
        internal_notes: notes,
      }
      store.dispatch('calendar/setCurrentEventInfo', updatedInfo)
      // Reset flag after store update is processed
      setTimeout(() => {
        isUpdatingFromStore.value = false;
      }, 0);
    }
  }
)
</script>


<style scoped>
.form-line{
  display: flex;
  align-items: start;
  gap: 12px;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.title-div{
  flex: 1;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  border-bottom: 1px solid #E0E0E0;
  padding-bottom: 8px;
}
/* .title-input{
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
}
.title-input::placeholder {
  color: #999999;
  font-weight: 400;
} */
.select-date-div{
  background-color: #F2F2F2;
  border-radius: 5px;
  padding: 6px 8px;
  cursor: pointer;
  height: 38px;
}
.divider-rect {
  width: 14px;
  height: 2px;
  background: #999999;
  align-self: center;
  /* margin: 0 4px; */
}

.timezone-row {
  display: flex;
  gap: 12px;
  align-items: center;
  /* margin-top: 8px; */
}

.timezone-icon-button {
  cursor: pointer;
}

.timezone-icon {
  transform: none;
  animation: none;
}

.timezone-expanded {
  overflow: hidden;
}

.timezone-expand-enter-active,
.timezone-expand-leave-active {
  transition: max-height 0.5s ease, opacity 0.5s ease, transform 0.5s ease;
  overflow: hidden;
}

.timezone-expand-enter-from,
.timezone-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.timezone-expand-enter-to,
.timezone-expand-leave-from {
  max-height: 120px;
  opacity: 1;
  transform: translateY(0);
}



.recurrence-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.repeat-row {
  width: 200px;
}

.until-label {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #333;
  letter-spacing: 0.15px;
}

.until-date-picker {
  width: 150px;
}

.remove-recurrence-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.choose-end-date-link {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.15px;
  color: #004879;
  text-decoration: underline;
  cursor: pointer;
}


.suggested-time {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid #E6E6E6;
  border-radius: 4px;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0.13px;
  cursor: pointer;
  color: #333;
}
.suggested-time:hover {
  border-color: #004879;
  background-color: #F9FBFD;
}
.suggested-time.selected {
  border-color: #004879;
  color: #004879;
  background-color: #F9FBFD;
}



.patient-select :deep(.el-select__wrapper),
.provider-select :deep(.el-select__wrapper) {

  box-shadow: none !important;
  border-bottom: 1px solid #E0E0E0;
  border-radius: 0px;
  /* padding: 0; */

}
.time-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border:none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 6px 8px 6px 12px;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder),  
.type-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder.is-transparent),
.type-select :deep(.el-select__selected-item.el-select__placeholder.is-transparent){
  color: #666;
}


.type-select :deep(.el-select__wrapper){
  height: 36px;

}


.timezone-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border: none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 4px 8px;
}

.timezone-select :deep(.el-select__placeholder.is-transparent) {
  color: #666;
}

.repeat-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border: none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 8px 8px 8px 12px;
}
.repeat-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}
.repeat-select :deep(.el-select__placeholder.is-transparent) {
  color: #666;
}
.description-input :deep(.el-input__wrapper){
  border:none;
  box-shadow: none !important;
  padding: 0 0 4px 0;
  height: 32px;
  border-radius: 0px;
  border-bottom: 1px solid #E0E0E0;
}
.description-input :deep(.el-input__inner){
  font-size: 14px;
  font-family: Inter;
  padding: 0;
}

/* Internal Notes: underline-only textarea matching the title/location inputs */
.internal-notes-input :deep(.el-textarea__inner){
  border: none;
  border-bottom: 1px solid #E0E0E0;
  border-radius: 0;
  box-shadow: none !important;
  padding: 0 0 8px 0;
  min-height: 32px !important;
  font-size: 14px;
  font-family: Inter;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
}
.internal-notes-input :deep(.el-textarea__inner::placeholder){
  color: #666;
}

</style>

  